import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Magnet, Move, Minimize2, Repeat } from "lucide-react";
import { WHY_MAGNETIC } from "@/data/site";
import { Reveal } from "@/components/Reveal";
import { SectionHeader } from "@/components/Primitives";

const BENEFITS = [
  { icon: Move, title: "Easy positioning", desc: "Find the spot that works — a chassis rail, a toolbox lid, a trailer drawbar — and place it." },
  { icon: Magnet, title: "Convenient attachment", desc: "No drilling, cable ties or adhesive on the vehicle. Metal sheets included for non-metal surfaces." },
  { icon: Minimize2, title: "Compact design", desc: "Small and low-profile, so it stays out of the way and out of sight." },
  { icon: Repeat, title: "Flexible use", desc: "Move it from the trailer to the toolbox to the suitcase as your week changes." },
];

const WhyMagnetic = () => {
  const [active, setActive] = useState(WHY_MAGNETIC[2]);
  return (
    <section data-testid="why-magnetic-section" className="px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
      <div className="mx-auto max-w-7xl rounded-[2.5rem] border border-black/5 bg-tracksy-cream px-6 py-14 sm:px-10 lg:px-16 lg:py-20">
        <SectionHeader eyebrow="Why magnetic?" title="Built to Go Where You Need It." desc="The magnetic holder is what makes Tracksy different: position it where it's useful, then move it when your needs change." className="mb-12" />
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="relative overflow-hidden rounded-[2rem] bg-white shadow-lift">
                <AnimatePresence mode="popLayout" initial={false}>
                  <motion.img
                    key={active.id}
                    data-testid="why-magnetic-image"
                    src={active.img}
                    alt={`Tracksy attached to a ${active.label.toLowerCase()}`}
                    initial={{ opacity: 0, scale: 1.04 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    className="aspect-[4/3] w-full object-cover"
                  />
                </AnimatePresence>
                <div className="absolute bottom-4 left-4 rounded-full bg-white/90 px-3.5 py-1.5 text-xs font-bold text-tracksy-ink backdrop-blur-xl">{active.label}</div>
              </div>
            </Reveal>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="why-magnetic-tabs">
              {WHY_MAGNETIC.map((w) => (
                <button
                  key={w.id}
                  type="button"
                  data-testid={`why-magnetic-tab-${w.id}`}
                  onClick={() => setActive(w)}
                  className={`rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wide transition-colors ${
                    active.id === w.id ? "bg-tracksy-ink text-white" : "bg-white text-tracksy-sub hover:text-tracksy-ink"
                  }`}
                >
                  {w.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-5 lg:grid-cols-1">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.07}>
                <div className="flex gap-4 rounded-2xl bg-white p-5 shadow-subtle">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-tracksy-orange/10">
                    <b.icon className="h-5 w-5 text-tracksy-orange" />
                  </span>
                  <div>
                    <p className="font-heading text-base font-bold text-tracksy-ink">{b.title}</p>
                    <p className="mt-1 text-sm leading-relaxed text-tracksy-sub">{b.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyMagnetic;
